'use client';

import { useEffect, useState } from 'react';
import dynamicImport from 'next/dynamic';
import Script from 'next/script';
import { getBikeparks } from '@/lib/api';
import { Bikepark } from '@/types';
import MapSidebar from '@/components/MapSidebar';
import MapLoadingSkeleton from '@/components/MapLoadingSkeleton';
import { useTranslations, useLocale } from 'next-intl';

export const dynamic = 'force-dynamic';

const MapView = dynamicImport(() => import('@/components/MapView'), {
  ssr: false,
  loading: () => <MapLoadingSkeleton />,
});

export default function BikeparksPage() {
  const t = useTranslations('map');
  const tCommon = useTranslations('common');
  const locale = useLocale();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Start",
        "item": `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Bikeparki",
        "item": `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}/bikeparki`
      }
    ]
  };

  const [allBikeparks, setAllBikeparks] = useState<Bikepark[]>([]);
  const [filteredBikeparks, setFilteredBikeparks] = useState<Bikepark[]>([]);
  const [selectedPark, setSelectedPark] = useState<Bikepark | null>(null);
  const [hoveredPark, setHoveredPark] = useState<Bikepark | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadBikeparks() {
      console.log('[BikeparksPage] Starting to load bikeparks...');
      const data = await getBikeparks(locale);
      console.log('[BikeparksPage] Loaded bikeparks:', data);
      console.log('[BikeparksPage] Bikeparks count:', data.length);
      setAllBikeparks(data);
      setFilteredBikeparks(data);
      setLoading(false);
    }
    loadBikeparks();
  }, [locale]);

  const handleParkClick = (park: Bikepark) => {
    setSelectedPark(park);
  };

  const handleParkHover = (park: Bikepark) => {
    setHoveredPark(park);
  };

  const handleParkHoverEnd = () => {
    setHoveredPark(null);
  };

  if (loading) {
    return (
      <div className="bp-loading">
        <div className="bp-loading-content">
          <h2>{t('loading')}</h2>
          <p>{t('loadingApi')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bp-app bp-layout-2col">
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {/* Main Content - Sidebar + Map */}
      <div className="bp-main-content">
        <MapSidebar
          parks={allBikeparks}
          filteredParks={filteredBikeparks}
          onFilterChange={setFilteredBikeparks}
          onParkClick={handleParkClick}
          onParkHover={handleParkHover}
          onParkHoverEnd={handleParkHoverEnd}
          type="bikepark"
        />

        <div className="bp-mapwrap">
          <MapView
            parks={filteredBikeparks}
            center={[51.9194, 19.1451]}
            zoom={6}
            type="bikepark"
            selectedPark={selectedPark}
            hoveredPark={hoveredPark}
          />
        </div>
      </div>

      <style jsx>{`
        /**
         * Clean layout for map view
         * Header removed - navigation moved to global Navigation component
         */
        .bp-layout-2col {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 110px);
          background: transparent;
        }

        @media (max-width: 768px) {
          .bp-layout-2col {
            height: calc(100vh - 90px);
          }
        }

        .bp-main-content {
          flex: 1;
          display: flex;
          overflow: hidden;
          position: relative;
        }

        .bp-mapwrap {
          flex: 1;
          position: relative;
        }
      `}</style>
    </div>
  );
}
